import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { TariffDto, TariffGettingInfo, TariffName } from '../../../models';
import { TariffInfo } from '../../../enums';
import { TariffsHandlerUtilities } from './tariffs-handler.utilities';
import { AccountService } from '../account.service';
import { RestService } from '../../requests';

@Injectable({
  providedIn: 'root',
})
export class TariffsHandlerService {
  private readonly accountService = inject(AccountService);

  constructor(private restService: RestService) {}
  private readonly contactCreditsPerUser = TariffInfo.contactCreditsPerUser;
  private readonly exportCreditsPerUser = TariffInfo.exportCreditsPerUser;
  private currentTariff: TariffDto;
  private nextTariff: TariffDto;
  private info: TariffGettingInfo;

  private currentTariffSubject = new BehaviorSubject<TariffDto>(null);
  private nextTariffSubject = new BehaviorSubject<TariffDto>(null);
  private accountUserNumberSubject = new BehaviorSubject<number>(1);

  currentTariff$: Observable<TariffDto> = this.currentTariffSubject.asObservable();
  nextTariff$: Observable<TariffDto> = this.nextTariffSubject.asObservable();
  accountUserNumber$ = this.accountUserNumberSubject.asObservable();

  getTariffParsedInfoByName(name: TariffName, isItNextAfterTrial: boolean = false): TariffDto {
    if (name === 'free' || (!name && isItNextAfterTrial)) {
      return this.getFreeTariffInfo(name);
    }

    return TariffsHandlerUtilities.getTariffByName(name);
  }

  updateCurrentTariff(tariff: TariffDto): void {
    this.currentTariff = tariff;

    this.currentTariffSubject.next(tariff);
  }

  updateNextTariff(tariff: TariffDto): void {
    this.nextTariff = tariff;

    this.nextTariffSubject.next(tariff);
  }

  dispatchTariffs(info: TariffGettingInfo): void {
    this.info = info;

    this.handleCurrentAndNextTariffs(info);
  }

  resetSystemTariffsInfo(): void {
    this.updateCurrentTariff(null);
    this.updateNextTariff(null);
  }

  updateAccountUserNumber(userNumber: number): void {
    this.accountUserNumberSubject.next(userNumber);
  }

  // /service/v2/price returns a single tariff priced for the requested params.
  getPricesAsset(ldSlots: number, month: number, userCount: number): Observable<TariffDto> {
    return this.restService.Service().priceV2(ldSlots, month, userCount);
  }

  // Free-plan limits come from `account.tariffFree` (a standard parseable tariff
  // code, e.g. u1_m0_c0_p0_e0_l0); the hardcoded zeros remain only as a fallback
  // for legacy payloads without it. `name` is kept as passed ('free' / empty) so
  // downstream isTariffFree checks on the DTO keep working.
  private getFreeTariffInfo(name: TariffName): TariffDto {
    const freeTariffName = this.accountService.currentAccount?.tariffFree;
    if (freeTariffName) {
      return { ...TariffsHandlerUtilities.getTariffByName(freeTariffName), name };
    }

    return {
      name,
      users: 1,
      grains: this.contactCreditsPerUser,
      price: 0,
      exportCredits: this.exportCreditsPerUser,
      linkedinSlots: 0,
    };
  }

  private handleCurrentAndNextTariffs(info: TariffGettingInfo): void {
    const { currentTariff, nextTariff } = info;

    this.updateCurrentTariff(currentTariff);
    this.updateNextTariff(nextTariff);
  }
}
